import { createFileRoute } from "@tanstack/react-router";
import type { FormEvent } from "react";
import { useId, useMemo, useState } from "react";
import {
	AuthError,
	AuthHeader,
	AuthPageShell,
	AuthSwitchLink,
	Divider,
	GoogleButton,
	PasswordInput,
	PasswordStrength,
	SubmitButton,
	TermsNotice,
	TextInput,
} from "@/components/auth/auth-ui";
import { LoadingScreen } from "@/components/LoadingScreen";
import { useWebHaptics } from "@/lib/haptics";
import {
	clearPendingAuthProvider,
	getLastAuthProvider,
	setLastAuthProvider,
	setPendingAuthProvider,
} from "@/lib/last-auth-provider";
import { noIndexMeta } from "@/lib/seo";
import { useSession } from "../hooks/auth-hooks";
import { signIn, signUp } from "../lib/auth-client";

export const Route = createFileRoute("/signup")({
	component: SignupPage,
	head: () => ({
		meta: [{ title: "AgentFramer | Create your account" }, ...noIndexMeta()],
	}),
});

export function SignupPage() {
	const { trigger } = useWebHaptics();
	const { session, user, isPending, refetch } = useSession();
	const [email, setEmail] = useState("");
	const [password, setPassword] = useState("");
	const [showPassword, setShowPassword] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [emailLoading, setEmailLoading] = useState(false);
	const [googleLoading, setGoogleLoading] = useState(false);
	const emailId = useId();
	const passwordId = useId();
	const errorId = useId();
	const strengthId = useId();
	const redirect = getRedirectParam();
	const postAuthUrl = redirect || "/dashboard";
	const lastProvider = getLastAuthProvider();
	const passwordStrength = useMemo(
		() => getPasswordStrength(password),
		[password],
	);

	if (!isPending && session && user) {
		if (typeof window !== "undefined") window.location.assign(postAuthUrl);
		return null;
	}
	if (isPending) return <LoadingScreen />;

	const handleEmailSignup = async (event: FormEvent<HTMLFormElement>) => {
		event.preventDefault();
		setError(null);
		setEmailLoading(true);

		try {
			const result = await signUp.email({
				email,
				password,
				name: email.split("@")[0],
				callbackURL: postAuthUrl,
			});

			if (result.error) {
				setError(result.error.message || "Failed to sign up");
				return;
			}

			setLastAuthProvider("email");
			trigger("success");
			await refetch();
			window.location.assign(postAuthUrl);
		} catch (err) {
			setError(err instanceof Error ? err.message : "An error occurred");
		} finally {
			setEmailLoading(false);
		}
	};

	const handleGoogleSignIn = async () => {
		setError(null);
		setGoogleLoading(true);
		setPendingAuthProvider("google");
		trigger("success");

		try {
			await signIn.social({ provider: "google", callbackURL: postAuthUrl });
		} catch (err) {
			clearPendingAuthProvider();
			setError(
				err instanceof Error ? err.message : "Failed to sign in with Google",
			);
			setGoogleLoading(false);
		}
	};

	return (
		<AuthPageShell>
			<AuthHeader
				eyebrow="Get started"
				headingId="signup-heading"
				title="Create your account"
				subtitle="Your agents can reason. Now let them create."
			/>

			{error && <AuthError id={errorId}>{error}</AuthError>}

			<GoogleButton
				loading={googleLoading}
				lastProvider={lastProvider}
				onClick={handleGoogleSignIn}
			/>
			<Divider />

			<form
				onSubmit={handleEmailSignup}
				aria-labelledby="signup-heading"
				className="space-y-4"
			>
				<TextInput
					id={emailId}
					label="Email"
					type="email"
					autoComplete="email"
					value={email}
					onChange={setEmail}
					placeholder="name@company.com"
					errorId={error ? errorId : undefined}
				/>
				<PasswordInput
					id={passwordId}
					autoComplete="new-password"
					value={password}
					onChange={setPassword}
					showPassword={showPassword}
					onTogglePassword={() => setShowPassword((value) => !value)}
					placeholder="Create a password"
					errorId={error ? errorId : undefined}
					describedBy={password ? strengthId : error ? errorId : undefined}
				/>
				{password && (
					<PasswordStrength id={strengthId} strength={passwordStrength} />
				)}
				<SubmitButton loading={emailLoading} loadingLabel="Creating account...">
					Create account
				</SubmitButton>
				<TermsNotice />
			</form>

			<AuthSwitchLink
				question="Already have an account?"
				linkText="Log in"
				to="/login"
				redirect={redirect}
			/>
		</AuthPageShell>
	);
}

function getPasswordStrength(password: string) {
	let score = 0;
	if (password.length >= 8) score++;
	if (password.length >= 12) score++;
	if (/[a-z]/.test(password) && /[A-Z]/.test(password)) score++;
	if (/\d/.test(password)) score++;
	if (/[^a-zA-Z0-9]/.test(password)) score++;

	if (score <= 1) return { score: 1, label: "Weak", kmColor: "#ff6e84" };
	if (score <= 2) return { score: 2, label: "Fair", kmColor: "#f59e0b" };
	if (score <= 3) return { score: 3, label: "Medium", kmColor: "#9D7CFF" };
	if (score <= 4) return { score: 4, label: "Strong", kmColor: "#2b9a66" };
	return { score: 5, label: "Very Strong", kmColor: "#2b9a66" };
}

function getRedirectParam() {
	if (typeof window === "undefined") return null;
	return new URLSearchParams(window.location.search).get("redirect");
}
